import cn from 'classnames'
import { IProfilingCode } from 'legacy/lut/classes/models/profiling/codes'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { PPA_FLAG } from 'clickNgo/entities/contactFlag'
import {
  ContactFlagRequestAction,
  IContactFlag,
  IToolsCodeRules,
} from 'clickNgo/entities/contactFlag'
import { ProfilingCodeType } from 'clickNgo/entities/profilingCode'
import { ConfirmDialog } from 'cng-common/components/confirmDialog'
import { InfoBlock, InfoBlockType } from 'cng-common/components/infoBlock'
import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { RenderWhen } from 'cng-common/components/renderWhen'
import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { Input } from 'core-components/library/inputs'
import { List } from 'shared/components/list'

import { AvailableFlag } from './components/availableFlag/AvailableFlag'
import { ContactFlag } from './components/contactFlag'
import { EmptyListItem } from './components/emptyListItem'
import { useGetAvailableFlags } from './hooks'
import { useGetUserRightRules } from './hooks/useGetUserRightRules'
import { IContactFlagsProps } from './models'
import { TRANSLATION_KEYS } from './translations'

import styles from './ContactFlags.module.scss'

const normalizeFlagCode = (code: string) => code.toLowerCase().trim()

export const ContactFlags = memo<IContactFlagsProps>(
  ({
    hasPortalAccess,
    contact,
    contactFlags,
    onAdd,
    onDelete,
    type,
    market,
    region,
    isDisabled,
    isReadOnly,
  }) => {
    const { partnerId, topPartnerId, country } = contact
    const [selectedContactFlags, setSelectedContactFlags] = useState<IContactFlag[]>([])
    const [selectedAvailableFlags, setSelectedAvailableFlags] = useState<IProfilingCode[]>([])
    const [searchValue, setSearchValue] = useState('')
    const [isIQWarningVisible, setIsIQWarningVisible] = useState(false)
    const [isPPAWarningVisible, setIsPPAWarningVisible] = useState(false)
    const [isBlockedFlagWarningVisible, setIsBlockedFlagWarningVisible] = useState(false)

    const isHQ = topPartnerId === partnerId
    const {
      availableFlags,
      profilingCodes,
      isLoading: isAvailableFlagsLoading,
    } = useGetAvailableFlags({
      contactFlags,
      partnerId,
      market,
      region,
      type,
      isHQ,
      skip: isReadOnly,
      country,
    })

    const { userRightRules, isLoading: isLoadingBlockedFlags } = useGetUserRightRules({ type })

    const blockedFlagsRef = useRef<IToolsCodeRules[]>([])

    const isToolsSection = useMemo(() => {
      return type === ProfilingCodeType.Tools
    }, [type])

    const profilingCodeNamesByCode = useMemo(() => {
      return profilingCodes.reduce<Map<string, string>>((acc, profilingCode) => {
        if (profilingCode.codeName) {
          acc.set(normalizeFlagCode(profilingCode.code), profilingCode.codeName)
        }

        return acc
      }, new Map())
    }, [profilingCodes])

    const getContactFlagCaption = useCallback(
      (flag: IContactFlag) => {
        return (
          profilingCodeNamesByCode.get(normalizeFlagCode(flag.code)) || flag.caption || flag.code
        )
      },
      [profilingCodeNamesByCode]
    )

    const filteredContactFlags = useMemo(() => {
      const normalizedSearchValue = searchValue.toLowerCase()

      return contactFlags.filter((flag) => {
        const caption = getContactFlagCaption(flag)

        return (
          caption.toLowerCase().includes(normalizedSearchValue) ||
          flag.code.toLowerCase().includes(normalizedSearchValue)
        )
      })
    }, [contactFlags, getContactFlagCaption, searchValue])

    const filteredAvailableFlags = useMemo(() => {
      const normalizedSearchValue = searchValue.toLowerCase()

      return availableFlags.filter((flag) => {
        const caption = flag.codeName || flag.code

        return (
          caption.toLowerCase().includes(normalizedSearchValue) ||
          flag.code.toLowerCase().includes(normalizedSearchValue)
        )
      })
    }, [availableFlags, searchValue])

    const isContactFlagsDisabled = selectedAvailableFlags.length !== 0 || isDisabled
    const isAvailableFlagsDisabled = selectedContactFlags.length !== 0 || isDisabled
    const isContactFlagsListEmpty = filteredContactFlags.length === 0
    const isAvailableFlagsListEmpty = filteredAvailableFlags.length === 0

    const hasHPID = useMemo(() => {
      return Boolean(contact.hpPassportProfileId)
    }, [contact.hpPassportProfileId])

    const handleSearch = useCallback((value: string) => {
      setSearchValue(value)
    }, [])

    const checkAndWarnBlockedFlag = useCallback(
      (flagCode: string) => {
        return userRightRules.some((el) => el.flagCode.toLowerCase() === flagCode.toLowerCase())
      },
      [userRightRules]
    )

    const handleSelectContactFlag = useCallback(
      (flag: IContactFlag) => (isSelected: boolean) => {
        if (checkAndWarnBlockedFlag(flag.code)) {
          setIsBlockedFlagWarningVisible(true)
        } else {
          setSelectedContactFlags((prev) =>
            isSelected ? [...prev, flag] : prev.filter((f) => f.code !== flag.code)
          )
        }
      },
      [checkAndWarnBlockedFlag]
    )

    const handleSelectAvailableContactFlag = useCallback(
      (flag: IProfilingCode) => (isSelected: boolean) => {
        if (checkAndWarnBlockedFlag(flag.code)) {
          setIsBlockedFlagWarningVisible(true)
        } else {
          const hasIQFlags = flag.code.toLowerCase().startsWith('iq')
          if (isSelected && !hasHPID && isToolsSection && hasIQFlags) {
            setIsIQWarningVisible(true)
          }
          setSelectedAvailableFlags((prev) =>
            isSelected ? [...prev, flag] : prev.filter((f) => f.entryId !== flag.entryId)
          )
        }
      },
      [hasHPID, isToolsSection, checkAndWarnBlockedFlag]
    )

    const handleResetAll = useCallback(() => {
      isAvailableFlagsDisabled && setSelectedContactFlags([])
      isContactFlagsDisabled && setSelectedAvailableFlags([])
    }, [isAvailableFlagsDisabled, isContactFlagsDisabled])

    const handleOnDelete = useCallback(() => {
      if (onDelete) {
        onDelete(
          selectedContactFlags.map((flag) => ({
            ...flag,
            market,
            region,
            country: contact.country,
            partnerId: contact.partnerId,
            action: ContactFlagRequestAction.Delete,
          }))
        )
      }

      setSelectedContactFlags([])
    }, [contact.country, contact.partnerId, market, onDelete, region, selectedContactFlags])

    const handleOnAdd = useCallback(async () => {
      const newContactFlags = selectedAvailableFlags.map(
        (profilingCode): IContactFlag => ({
          contactId: contact.contactId,
          entryId: 0,
          market,
          country: contact.country,
          code: profilingCode.code,
          partnerId: contact.partnerId,
          region,
          action: ContactFlagRequestAction.Add,
          type,
        })
      )

      if (onAdd) {
        await onAdd(newContactFlags)
      }

      const isPPAFlagAdded =
        type === ProfilingCodeType.Tools &&
        newContactFlags.some((flag) => PPA_FLAG.includes(flag.code))

      if (isPPAFlagAdded && !contact.userLastLoginDate) {
        setIsPPAWarningVisible(true)
      }

      setSelectedAvailableFlags([])
    }, [
      contact.contactId,
      contact.country,
      contact.partnerId,
      contact.userLastLoginDate,
      market,
      onAdd,
      region,
      selectedAvailableFlags,
      type,
    ])

    const handleBlockedFlagWarningClose = useCallback(() => {
      setIsBlockedFlagWarningVisible(false)
    }, [])

    const handlePPAWarningClose = useCallback(() => {
      setIsPPAWarningVisible(false)
    }, [])

    const renderContactFlags = useCallback(() => {
      if (isContactFlagsListEmpty) {
        return <EmptyListItem />
      } else {
        return filteredContactFlags.map((flag) => {
          const onSelect = handleSelectContactFlag(flag)
          const isSelected = selectedContactFlags.some((f) => f.code === flag.code)
          return (
            <ContactFlag
              key={flag.code}
              flag={flag}
              caption={getContactFlagCaption(flag)}
              onSelect={onSelect}
              isSelected={isSelected}
              isDisabled={isContactFlagsDisabled || isLoadingBlockedFlags}
              isReadOnly={isReadOnly}
            />
          )
        })
      }
    }, [
      filteredContactFlags,
      getContactFlagCaption,
      handleSelectContactFlag,
      isContactFlagsDisabled,
      isContactFlagsListEmpty,
      isLoadingBlockedFlags,
      isReadOnly,
      selectedContactFlags,
    ])

    const renderAvailableContactFlags = useCallback(() => {
      if (isAvailableFlagsListEmpty) {
        return <EmptyListItem />
      } else {
        return filteredAvailableFlags.map((flag) => {
          const onSelect = handleSelectAvailableContactFlag(flag)
          const isSelected = selectedAvailableFlags.some((f) => f.entryId === flag.entryId)
          return (
            <AvailableFlag
              key={flag.code}
              flag={flag}
              onSelect={onSelect}
              isSelected={isSelected}
              isDisabled={isAvailableFlagsDisabled || isLoadingBlockedFlags}
              isReadOnly={isReadOnly}
            />
          )
        })
      }
    }, [
      filteredAvailableFlags,
      handleSelectAvailableContactFlag,
      isAvailableFlagsDisabled,
      isAvailableFlagsListEmpty,
      isLoadingBlockedFlags,
      isReadOnly,
      selectedAvailableFlags,
    ])

    useEffect(() => {
      blockedFlagsRef.current = userRightRules
    }, [userRightRules])

    return (
      <>
        <LoaderOverlay
          isVisible={isAvailableFlagsLoading || isLoadingBlockedFlags}
          message="Updating contact flag list..."
        />
        <div className={styles.container}>
          <div className={styles.search}>
            <Input placeholder="Search" onChange={handleSearch} value={searchValue} />
          </div>
          {!isReadOnly && !hasPortalAccess && isToolsSection && (
            <InfoBlock
              text={<Translation text={TRANSLATION_KEYS.noPortalAccessWarning} />}
              type={InfoBlockType.Warning}
            />
          )}
          <div className={styles.section}>
            <div
              className={cn(styles.listContainer, { [styles.disabled]: isContactFlagsDisabled })}
              style={{ gridColumn: isReadOnly ? 'span 2' : 'span 1' }}
            >
              <div className={styles.listInfo}>
                <div className={styles.listInfoLeft}>
                  <strong className={styles.listTitle}>Contact Flags</strong>
                  <h6>Code: </h6>
                </div>
                <div className={styles.listInfoRight}>
                  <RenderWhen condition={!isAvailableFlagsDisabled}>
                    <h6>Request info: </h6>
                  </RenderWhen>
                  <RenderWhen condition={isAvailableFlagsDisabled}>
                    <div className={styles.listAction}>
                      <Button color="danger" icon="kbase kbase-delete" onClick={handleOnDelete}>
                        Delete
                      </Button>
                      <Button onClick={handleResetAll} variant="text" icon="kbase kbase-reload">
                        Reset all
                      </Button>
                    </div>
                  </RenderWhen>
                </div>
              </div>
              <List className={cn(styles.list, { [styles.empty]: isContactFlagsListEmpty })}>
                {renderContactFlags()}
              </List>
            </div>
            <RenderWhen condition={!isReadOnly}>
              <div
                className={cn(styles.listContainer, {
                  [styles.disabled]: isAvailableFlagsDisabled,
                })}
              >
                <div className={styles.listInfo}>
                  <div className={styles.listInfoLeft}>
                    <strong className={styles.listTitle}>Available Flags</strong>
                    <h6>Code: </h6>
                  </div>
                  <div className={styles.listInfoRight}>
                    <RenderWhen condition={isContactFlagsDisabled}>
                      <div className={styles.listAction}>
                        <Button icon="kbase kbase-add" onClick={handleOnAdd}>
                          Add
                        </Button>
                        <Button onClick={handleResetAll} variant="text" icon="kbase kbase-reload">
                          Reset all
                        </Button>
                      </div>
                    </RenderWhen>
                  </div>
                </div>
                <List className={cn(styles.list, { [styles.empty]: isAvailableFlagsListEmpty })}>
                  {renderAvailableContactFlags()}
                </List>
                <ConfirmDialog
                  size="xs"
                  isOnlyApply
                  confirmBtnText={TRANSLATION_KEYS.dialog.iqWarning.confirm}
                  content={TRANSLATION_KEYS.dialog.iqWarning.title}
                  isVisible={isIQWarningVisible}
                  onConfirm={() => setIsIQWarningVisible(false)}
                  title={''}
                />
                <ConfirmDialog
                  size="xs"
                  isOnlyApply
                  confirmBtnText={TRANSLATION_KEYS.dialog.blockedFlagWarning.confirm}
                  content={TRANSLATION_KEYS.dialog.blockedFlagWarning.message}
                  isVisible={isBlockedFlagWarningVisible}
                  onConfirm={handleBlockedFlagWarningClose}
                  title={''}
                />
              </div>
            </RenderWhen>
          </div>
        </div>
        <ConfirmDialog
          isVisible={isPPAWarningVisible}
          onConfirm={handlePPAWarningClose}
          title={TRANSLATION_KEYS.dialog.ppaWarning.title}
          content={TRANSLATION_KEYS.dialog.ppaWarning.message}
          confirmBtnText={TRANSLATION_KEYS.dialog.ppaWarning.confirm}
          isOnlyApply
        />
      </>
    )
  }
)

ContactFlags.displayName = 'ContactFlags'
